import {useEffect, useMemo} from 'react';
import moment from 'moment';
import {
    APPLICATION_NAMES, SURVICATE_SDK_BASE_URL, THEMES
} from '@constants/main';
import isClient from '@utils/isClient';
import {
    SurvicateWindow,
    SCRIPT_IDS,
    AccountProperties,
    ThemeMode,
    SURVICATE_READY_EVENT
} from './types';

declare const window: SurvicateWindow;

const getThemeMode = (theme: string): ThemeMode => theme === THEMES.DARK ? 'dark' : 'light';

/**
 * Sent as a trait so the "show only after N days" rule lives in the Survicate audience settings
 * instead of being hardcoded here.
 */
const getDaysAfterCreation = (createdDate: string): number => (
    createdDate ? moment().diff(moment(createdDate), 'days') : 0
);

export const useInitializeSurvicateScript = ({
    userId,
    userEmail,
    locale,
    createdDate,
    accountProperties,
    isSender,
    survicateWorkspaceKey,
    environment,
    theme,
    userRole,
    features
}: {
    userId: string;
    userEmail: string;
    locale: string;
    createdDate: string;
    accountProperties: AccountProperties;
    isSender: boolean;
    survicateWorkspaceKey: string;
    environment: string;
    theme: string;
    userRole: string;
    features: string;
}): void => {
    const {
        country, company, accountId, subscriptionPlan
    } = accountProperties;
    const themeMode = getThemeMode(theme);

    const traits = useMemo(() => ({
        user_id: userId,
        email: userEmail,
        created_at: createdDate,
        application: isSender ? APPLICATION_NAMES.SENDER_UI : APPLICATION_NAMES.SIGNER_UI,
        country,
        company,
        account_id: accountId,
        subscription_plan: subscriptionPlan,
        environment,
        user_role: userRole,
        language: locale,
        features,
        daysAfterCreation: getDaysAfterCreation(createdDate)
    }), [
        userId,
        userEmail,
        createdDate,
        isSender,
        country,
        company,
        accountId,
        subscriptionPlan,
        environment,
        userRole,
        locale,
        features
    ]);

    useEffect(() => {
        if (!userId || !userEmail || !survicateWorkspaceKey || !createdDate || !isClient || navigator.webdriver) {
            return;
        }

        if (document.getElementById(SCRIPT_IDS.BEACON)) {
            return;
        }

        window._sva = {
            ...window._sva,
            traits,
            themeMode,
            forcedLanguage: locale
        };

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = SCRIPT_IDS.BEACON;
        script.async = true;
        script.src = `${SURVICATE_SDK_BASE_URL}/${survicateWorkspaceKey}/web_surveys.js`;
        script.onerror = () => {
            console.warn('Failed to load Survicate SDK');
        };

        document.body.appendChild(script);
        // The SDK keeps running for the lifetime of the page: removing the tag would not unload it,
        // while wiping the config it still reads (workspaceKey) breaks survey rendering.
    }, [
        userId,
        userEmail,
        survicateWorkspaceKey,
        createdDate,
        traits,
        themeMode,
        locale
    ]);

    useEffect(() => {
        const applyVisitorState = (): void => {
            window._sva?.setVisitorTraits?.(traits);
            window._sva?.setThemeMode?.(themeMode);
            window._sva?.setSurveyLanguage?.(locale);
        };

        applyVisitorState();
        window.addEventListener(SURVICATE_READY_EVENT, applyVisitorState);

        return () => window.removeEventListener(SURVICATE_READY_EVENT, applyVisitorState);
    }, [traits, themeMode, locale]);
};
